import styles from './ServiceContractPositionKindPicker.module.scss';

import PinIcon from '@mui/icons-material/PushPin';
import { ListItem, Radio, Typography } from '@mui/material';
import clsx from 'clsx';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { ServiceContractPositionKind } from '@work4all/models/lib/Enums/ServiceContractPositionKind.enum';

import { Tooltip } from '../../tooltip/Tooltip';
import { FixedDataPicker } from '../fixed-data-picker/FixedDataPicker';
import { IPickerProps } from '../types';

export const SERVICE_CONTRACT_POSITION_KIND = [
  ServiceContractPositionKind.UEBERNEHMEN_MIT_E_PREIS_AUS_WARTUNGS_OBJEKT,
  ServiceContractPositionKind.UEBERNEHMEN_MIT_WARTUNGSSUMME,
  ServiceContractPositionKind.UEBERNEHMEN_MIT_ARTIKELPREIS,
  ServiceContractPositionKind.UEBERNEHMEN_OHNE_PREIS,
  ServiceContractPositionKind.NICHT_UEBERNEHMEN,
];

export const STANDARD_POSITION_KIND = [
  ServiceContractPositionKind.UEBERNEHMEN_MIT_E_PREIS_AUS_WARTUNGS_OBJEKT,
  ServiceContractPositionKind.UEBERNEHMEN_MIT_WARTUNGSSUMME,
  ServiceContractPositionKind.UEBERNEHMEN_MIT_ARTIKELPREIS,
];

const SERVICE_CONTRACT_POSITION_KIND_WITH_DIVIDER = [
  ServiceContractPositionKind.UEBERNEHMEN_OHNE_PREIS,
];

export type ServiceContractPositionKindPickerProps = IPickerProps<
  { id: ServiceContractPositionKind; name: string },
  false
> & {
  onStandardChange: (value: ServiceContractPositionKind) => void;
  validateStandard: (value: ServiceContractPositionKind) => boolean;
};

export function ServiceContractPositionKindPicker(
  props: ServiceContractPositionKindPickerProps
) {
  const { t } = useTranslation();
  const { onChange, onStandardChange, validateStandard, ...restProps } = props;

  const data = useMemo(() => {
    return SERVICE_CONTRACT_POSITION_KIND.map((key) => {
      const id = ServiceContractPositionKind[key];

      return {
        id,
        name: t(`SERVICE_CONTRACT_POSITION_KINDS.${id}.FULL`, {
          defaultValue: t(`SERVICE_CONTRACT_POSITION_KINDS.${id}`),
        }),
      };
    });
  }, [t]);

  const renderItemContent = useCallback(
    (kind) => (
      <ListItem key={kind} disablePadding className={styles.listItem}>
        <Typography variant="body2" noWrap>
          {kind.name}
        </Typography>
        {STANDARD_POSITION_KIND.includes(kind.id) && (
          <Tooltip title={t('INPUTS.SET_STANDART')} placement="right">
            <Radio
              icon={<PinIcon className={styles.pinIcon} />}
              checkedIcon={
                <PinIcon
                  className={clsx(styles.pinIcon, styles.checkedPinIcon)}
                />
              }
              checked={validateStandard(kind.id)}
              onClick={(e) => {
                e.stopPropagation();
                onStandardChange(kind.id);
              }}
              value={kind}
              className={styles.listItemRadio}
              sx={{
                visibility: validateStandard(kind.id) ? 'visible' : 'hidden',
              }}
            />
          </Tooltip>
        )}
      </ListItem>
    ),
    [onStandardChange, t, validateStandard]
  );

  const handleChange = useCallback(
    (value) => onChange?.({ ...value }),
    [onChange]
  );

  return (
    <FixedDataPicker
      dataSet={data}
      optionsWithDivider={SERVICE_CONTRACT_POSITION_KIND_WITH_DIVIDER}
      renderItemContent={renderItemContent}
      clearable={false}
      onChange={handleChange}
      hideClearIcon={true}
      {...restProps}
    />
  );
}
